import { useMemo } from 'react'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { YearRangePicker } from './shared/YearRangePicker'
import { YlyStatsGrid } from './shared/YlyStatsGrid'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { withShortLabels } from './shared/rowGroupsTransposed'
import { useYearlyData } from '../../hooks/useYearlyData'
import { useYearlyNormals } from '../../hooks/useYearlyNormals'
import { useYearRangeStore } from '../../store/useYearRangeStore'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'

const SLUG = 'yly-teploty'

const ROW_GROUPS = [
  {
    label: 'Průměry',
    rows: [
      { tsId: 'TEMPAVG_AVG', label: 'Průměrná teplota',          unit: '°C', normalMode: 'diff', decimals: 1 },
      { tsId: 'TNMIN_AVG',   label: 'Průměr minimálních teplot', unit: '°C', normalMode: 'diff', decimals: 1 },
      { tsId: 'TXMAX_AVG',   label: 'Průměr maximálních teplot', unit: '°C', normalMode: 'diff', decimals: 1 },
    ],
  },
  {
    label: 'Extrémy',
    rows: [
      { tsId: 'TNMIN_MIN',      label: 'Absolutní minimum roku',     unit: '°C', normalMode: null, decimals: 1 },
      { tsId: 'TXMAX_MAX',      label: 'Absolutní maximum roku',      unit: '°C', normalMode: null, decimals: 1 },
      { tsId: 'TGROUNDMIN_MIN', label: 'Min. povrchové teploty',      unit: '°C', normalMode: null, decimals: 1 },
    ],
  },
  {
    label: 'Teplotní dny',
    rows: [
      { tsId: 'TXMAX_GE25', label: 'Letní dny – Tmax ≥ 25 °C',          unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'TXMAX_GE30', label: 'Horké dny – Tmax ≥ 30 °C',           unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'TXMAX_GE35', label: 'Velmi horké dny – Tmax ≥ 35 °C',     unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'TNMIN_GE20', label: 'Tropické noci – Tmin ≥ 20 °C',       unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'TNMIN_L0',   label: 'Mrazové dny – Tmin < 0 °C',          unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'TNMIN_L-10', label: 'Silné mrazy – Tmin < −10 °C',         unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'TXMAX_L0',   label: 'Ledové dny – Tmax < 0 °C',            unit: 'dny', normalMode: 'diff', decimals: 0 },
    ],
  },
]

// 2. definice hlavičky – transponovaná tabulka.
const ROW_GROUPS_T = withShortLabels(ROW_GROUPS, {
  TEMPAVG_AVG: 'Prům. teplota', TNMIN_AVG: 'Prům. min.', TXMAX_AVG: 'Prům. max.',
  TNMIN_MIN: 'Abs. min. roku', TXMAX_MAX: 'Abs. max. roku', TGROUNDMIN_MIN: 'Min. povrchová',
  TXMAX_GE25: 'Letní ≥ 25', TXMAX_GE30: 'Horké ≥ 30', TXMAX_GE35: 'Velmi horké ≥ 35',
  TNMIN_GE20: 'Trop. noci ≥ 20', TNMIN_L0: 'Mrazové < 0',
  'TNMIN_L-10': 'Silné mrazy < −10', TXMAX_L0: 'Ledové < 0',
})

const TS_IDS = ROW_GROUPS.flatMap(g => g.rows.map(r => r.tsId))

export function YlyTeplotyView({ station }) {
  const { yearFrom, yearTo, setYearFrom, setYearTo } = useYearRangeStore()
  const orientation = useTableOrientationStore((s) => s.getOrientation(SLUG))

  const years = useMemo(
    () => Array.from({ length: yearTo - yearFrom + 1 }, (_, i) => String(yearFrom + i)),
    [yearFrom, yearTo]
  )

  const { dataMap, isLoading: dLoading, isError: dError } = useYearlyData(station?.gh_id, yearFrom, yearTo, TS_IDS)
  const { normalsMap, isLoading: nLoading }               = useYearlyNormals(station?.gh_id, TS_IDS)

  const isLoading = dLoading || nLoading

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}
        <div className="flex items-center justify-between gap-3">
          <YearRangePicker yearFrom={yearFrom} yearTo={yearTo} onYearFromChange={setYearFrom} onYearToChange={setYearTo} />
          <TableOrientationToggle slug={SLUG} />
        </div>
      </div>
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {isLoading && <div className="flex items-center gap-2 text-sm text-gray-500"><Spinner size="sm" /> Načítání dat…</div>}
        {dError && <p className="text-red-500 text-sm">Chyba při načítání dat</p>}
        {!isLoading && !dError && (
          <YlyStatsGrid
            rowGroups={ROW_GROUPS}
            rowGroupsTransposed={ROW_GROUPS_T}
            orientation={orientation}
            dataMap={dataMap}
            normalsMap={normalsMap}
            years={years}
          />
        )}
      </div>
    </div>
  )
}
